"use client";

import React from "react";

import { useRouter } from "next/navigation";

import { Button } from "@heroui/button";
import { Card, CardBody, CardHeader } from "@heroui/card";
import { icons } from "lucide-react";
import { FieldValues } from "react-hook-form";

import { BaseFormTemplate } from "./BaseFormTemplate";
import { UnifiedFormTemplateProps } from "./formInterfaces";
import { FORM_LAYOUT } from "./formLayout";

import { SPACING } from "@/lib/constants/layoutConstants";
import { DEFAULT_TERMINOLOGY } from "@/lib/services/terminologyDefaults";

function shouldInterceptEnter(target: HTMLElement): boolean {
  const tagName = target.tagName.toLowerCase();

  return (
    tagName !== "textarea" && tagName !== "button" && !target.isContentEditable
  );
}

function handleEnterKeySubmit(
  event: React.KeyboardEvent<HTMLFormElement>,
): void {
  if (event.key !== "Enter") return;
  if (!shouldInterceptEnter(event.target as HTMLElement)) return;

  event.preventDefault();
  (event.currentTarget as HTMLFormElement).requestSubmit();
}

function buildFormId(title?: string): string {
  return `${title?.toLowerCase().replace(/\s+/g, "-") ?? "page"}-form`;
}

function resolveSubmitLabel(
  submitLabel?: string,
  terminology?: { termSubmit?: string },
): string {
  return (
    submitLabel ?? terminology?.termSubmit ?? DEFAULT_TERMINOLOGY.termSubmit
  );
}

function resolveIconComponent(icon?: React.ReactNode) {
  if (typeof icon !== "string") return null;

  return icons[icon as keyof typeof icons] ?? null;
}

interface PageFormActionsProps {
  cancelLabel: string;
  submitLabel: string;
  onCancel: () => void;
}

function PageFormActions({
  cancelLabel,
  onCancel,
  submitLabel,
}: PageFormActionsProps) {
  return (
    <div
      className={`flex justify-end ${FORM_LAYOUT.spacing.buttonGroup} ${FORM_LAYOUT.padding.buttonGroup}`}
    >
      <Button color="default" type="button" variant="light" onPress={onCancel}>
        {cancelLabel}
      </Button>
      <Button
        color="primary"
        data-testid="submit-button"
        type="submit"
        variant="solid"
      >
        {submitLabel}
      </Button>
    </div>
  );
}

export function PageFormTemplate<TFormValues extends FieldValues>(
  props: UnifiedFormTemplateProps<TFormValues>,
) {
  const {
    callbackUrl,
    cancelLabel = "Cancel",
    className = "",
    "data-testid": dataTestId,
    description,
    hideDefaultActions = false,
    icon,
    onCancel,
    terminology,
    title,
  } = props;
  const submitLabel = resolveSubmitLabel(props.submitLabel, terminology);
  const router = useRouter();
  const formId = buildFormId(title);
  const Icon = resolveIconComponent(icon);

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      router.back();
    }
  };

  const handleSuccess = (result: unknown) => {
    props.onSuccess?.(
      result as Parameters<NonNullable<typeof props.onSuccess>>[0],
    );
    if (callbackUrl) {
      setTimeout(() => {
        router.push(callbackUrl);
      }, 300);
    }
  };

  return (
    <BaseFormTemplate
      {...props}
      renderContainer={({ form }) => (
        <Card className={`shadow-sm border-default-200 ${className}`}>
          <CardHeader className={FORM_LAYOUT.header.className}>
            {Icon && (
              <div className="p-2 bg-primary-100 rounded-full">
                <Icon aria-hidden="true" className="h-5 w-5 text-primary-600" />
              </div>
            )}
            <div>
              <h2 className={FORM_LAYOUT.header.title} id={`${formId}-title`}>
                {title}
              </h2>
              <p className={FORM_LAYOUT.header.description}>{description}</p>
            </div>
          </CardHeader>
          <CardBody>{form}</CardBody>
        </Card>
      )}
      renderForm={({ children, onSubmit }) => (
        <form
          noValidate
          aria-labelledby={`${formId}-title`}
          className={SPACING.FORM_FIELD}
          data-testid={dataTestId}
          id={formId}
          method="POST"
          role="form"
          onKeyDown={handleEnterKeySubmit}
          onSubmit={onSubmit}
        >
          <button
            aria-hidden="true"
            style={{ display: "none" }}
            tabIndex={-1}
            type="submit"
          />
          {children}
          {!hideDefaultActions && (
            <PageFormActions
              cancelLabel={cancelLabel}
              submitLabel={submitLabel}
              onCancel={handleCancel}
            />
          )}
        </form>
      )}
      onSuccess={handleSuccess}
    />
  );
}
